import React, { useState, useEffect } from 'react';
import { useConfirm } from '../context/ConfirmContext';
import { Person, CustomerPerson, CreateCustomerPersonDto } from '../types';
import { personsAPI, customerPersonsAPI } from '../services/api';

interface PendingCustomerPerson {
  tempId: number;
  personId: number;
  personFirstName: string;
  personLastName: string;
  jobTitle?: string;
  department?: string;
  workingEmail?: string;
}

interface CustomerPersonsProps {
  customerId?: number;
  isEditing: boolean;
  onPendingPersonsChange?: (persons: CreateCustomerPersonDto[]) => void;
  onPersonsChange?: () => void;
}

const CustomerPersons: React.FC<CustomerPersonsProps> = ({ customerId, isEditing, onPendingPersonsChange, onPersonsChange }) => {
  const [persons, setPersons] = useState<Person[]>([]);
  const [customerPersons, setCustomerPersons] = useState<CustomerPerson[]>([]);
  const [pendingPersons, setPendingPersons] = useState<PendingCustomerPerson[]>([]);
  const [selectedPersonId, setSelectedPersonId] = useState<number>(0);
  const [jobTitle, setJobTitle] = useState<string>('');
  const [department, setDepartment] = useState<string>('');
  const [workingEmail, setWorkingEmail] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [tempIdCounter, setTempIdCounter] = useState(1);
  const confirm = useConfirm();

  useEffect(() => {
    loadPersons();
    if (customerId) {
      loadCustomerPersons();
    }
  }, [customerId]);

  // Notify parent of pending persons changes
  useEffect(() => {
    if (!customerId && onPendingPersonsChange) {
      const createPersonsData = pendingPersons.map(cp => ({
        customerId: 0, // Will be set when customer is created
        personId: cp.personId,
        jobTitle: cp.jobTitle,
        department: cp.department,
        workingEmail: cp.workingEmail
      }));
      onPendingPersonsChange(createPersonsData);
    }
  }, [pendingPersons, customerId, onPendingPersonsChange]);

  const loadPersons = async () => {
    try {
      const data = await personsAPI.getPersons();
      setPersons(data);
    } catch (error) {
      console.error('Error loading persons:', error);
    }
  };

  const loadCustomerPersons = async () => {
    if (!customerId) return;

    try {
      setLoading(true);
      const data = await customerPersonsAPI.getCustomerPersonsByCustomer(customerId);
      setCustomerPersons(data);
    } catch (error) {
      console.error('Error loading customer persons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!selectedPersonId) return;

    // If no customerId, add to pending list
    if (!customerId) {
      const selectedPerson = persons.find(p => p.id === selectedPersonId);
      if (!selectedPerson) return;

      const newPending: PendingCustomerPerson = {
        tempId: tempIdCounter,
        personId: selectedPersonId,
        personFirstName: selectedPerson.firstName,
        personLastName: selectedPerson.lastName,
        jobTitle: jobTitle || undefined,
        department: department || undefined,
        workingEmail: workingEmail || undefined
      };

      setPendingPersons([...pendingPersons, newPending]);
      setTempIdCounter(tempIdCounter + 1);
      setSelectedPersonId(0);
      setJobTitle('');
      setDepartment('');
      setWorkingEmail('');
      return;
    }

    try {
      setLoading(true);
      const newCustomerPerson: CreateCustomerPersonDto = {
        customerId,
        personId: selectedPersonId, 
        jobTitle: jobTitle || undefined,
        department: department || undefined,
        workingEmail: workingEmail || undefined
      };

      await customerPersonsAPI.createCustomerPerson(newCustomerPerson);
      await loadCustomerPersons();
      onPersonsChange?.();
      setSelectedPersonId(0);
      setJobTitle('');
      setDepartment('');
      setWorkingEmail('');
    } catch (error) {
      console.error('Error adding customer person:', error);
      alert('Failed to add person to customer');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (id: number) => {
    const customerPerson = customerPersons.find(cp => cp.id === id);
    if (!customerPerson) return;

    try {
      setLoading(true);
      await customerPersonsAPI.updateCustomerPerson(id, {
        id,
        customerId: customerPerson.customerId,
        personId: customerPerson.personId,
        isFormer: customerPerson.isFormer,
        jobTitle: jobTitle || undefined,
        department: department || undefined,
        workingEmail: workingEmail || undefined
      });
      await loadCustomerPersons();
      onPersonsChange?.();
      setEditingId(null);
      setJobTitle('');
      setDepartment('');
      setWorkingEmail('');
    } catch (error) {
      console.error('Error updating customer person:', error);
      alert('Failed to update person details');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    const isConfirmed = await confirm({
      title: 'Remove assigned person',
      message: 'Are you sure you want to remove this person from the customer?',
      confirmText: 'Remove',
      cancelText: 'Cancel',
      variant: 'danger'
    });
    if (!isConfirmed) return;

    try {
      setLoading(true);
      await customerPersonsAPI.deleteCustomerPerson(id);
      await loadCustomerPersons();
      onPersonsChange?.();
    } catch (error) {
      console.error('Error deleting customer person:', error);
      alert('Failed to remove person from customer');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePending = (tempId: number) => {
    const updatedPending = pendingPersons.map(cp =>
      cp.tempId === tempId ? {
        ...cp,
        jobTitle: jobTitle || undefined,
        department: department || undefined,
        workingEmail: workingEmail || undefined
      } : cp
    );
    setPendingPersons(updatedPending);
    setEditingId(null);
    setJobTitle('');
    setDepartment('');
    setWorkingEmail('');
  };

  const handleDeletePending = (tempId: number) => {
    setPendingPersons(pendingPersons.filter(cp => cp.tempId !== tempId));
  };

  const startEdit = (customerPerson: CustomerPerson) => {
    setEditingId(customerPerson.id);
    setJobTitle(customerPerson.jobTitle || '');
    setDepartment(customerPerson.department || '');
    setWorkingEmail(customerPerson.workingEmail || '');
  };

  const startEditPending = (pending: PendingCustomerPerson) => {
    setEditingId(pending.tempId);
    setJobTitle(pending.jobTitle || '');
    setDepartment(pending.department || '');
    setWorkingEmail(pending.workingEmail || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setJobTitle('');
    setDepartment('');
    setWorkingEmail('');
  };

  if (!isEditing) {
    return null;
  }

  // Person deletion is soft; assignment queries can still include their old links.
  const activePersonIds = new Set(persons.map(person => person.id));
  const existingCustomerPersons = customerPersons.filter(cp => activePersonIds.has(cp.personId));
  const assignedPersonIds = customerId
    ? existingCustomerPersons.map(cp => cp.personId)
    : pendingPersons.map(cp => cp.personId);

  const displayPersons = customerId ? existingCustomerPersons : pendingPersons;

  return (
    <div className="card">
      <div className="card-header">
        <h6 className="mb-0">Assigned Persons</h6>
      </div>
      <div className="card-body">
        <div className="row g-3 mb-3">
          <div className="col-md-3">
            <label htmlFor="personSelect" className="form-label">Person</label>
            <select
              id="personSelect"
              className="form-select"
              value={selectedPersonId}
              onChange={(e) => setSelectedPersonId(parseInt(e.target.value))}
              disabled={loading || editingId !== null}
            >
              <option value="0">Select person...</option>
              {persons
                .filter(p => !assignedPersonIds.includes(p.id))
                .map(person => (
                  <option key={person.id} value={person.id}>
                    {person.firstName} {person.lastName}
                  </option>
                ))}
            </select>
          </div>
          <div className="col-md-3">
            <label htmlFor="jobTitleInput" className="form-label">Job Title</label>
            <input
              type="text"
              id="jobTitleInput"
              className="form-control"
              placeholder="e.g., CEO, CTO..."
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              disabled={loading || editingId !== null}
            />
          </div>
          <div className="col-md-2">
            <label htmlFor="departmentInput" className="form-label">Department</label>
            <input
              type="text"
              id="departmentInput"
              className="form-control"
              placeholder="e.g., IT, Sales..."
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              disabled={loading || editingId !== null}
            />
          </div>
          <div className="col-md-3">
            <label htmlFor="workingEmailInput" className="form-label">Working Email</label>
            <input
              type="email"
              id="workingEmailInput"
              className="form-control"
              placeholder="email@company.com"
              value={workingEmail}
              onChange={(e) => setWorkingEmail(e.target.value)}
              disabled={loading || editingId !== null}
            />
          </div>
          <div className="col-md-1 d-flex align-items-end justify-content-end">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAdd}
              disabled={loading || editingId !== null || selectedPersonId === 0}
            >
              Add
            </button>
          </div>
        </div>

        {displayPersons.length === 0 ? (
          <div className="text-center text-muted py-3">
            No persons assigned to this customer yet.
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-light">
                <tr>
                  <th className="text-start">Name</th>
                  <th className="text-start">Job Title</th>
                  <th className="text-start">Department</th>
                  <th className="text-start">Working Email</th>
                  <th className="text-start" style={{ width: '150px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {customerId ? (
                  existingCustomerPersons.map(cp => (
                  <tr key={cp.id}>
                    <td>
                      {cp.personFirstName} {cp.personLastName}
                      {cp.isFormer && <div className="small text-muted">Former</div>}
                    </td>
                    <td>
                      {editingId === cp.id ? (
                        <input
                          type="text"
                          aria-label="Edit job title"
                          className="form-control form-control-sm"
                          value={jobTitle}
                          onChange={(e) => setJobTitle(e.target.value)}
                        />
                      ) : (
                        cp.jobTitle || <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {editingId === cp.id ? (
                        <input
                          type="text"
                          aria-label="Edit department"
                          className="form-control form-control-sm"
                          value={department}
                          onChange={(e) => setDepartment(e.target.value)}
                        />
                      ) : (
                        cp.department || <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {editingId === cp.id ? (
                        <input
                          type="email"
                          aria-label="Edit working email"
                          className="form-control form-control-sm"
                          value={workingEmail}
                          onChange={(e) => setWorkingEmail(e.target.value)}
                        />
                      ) : (
                        cp.workingEmail || <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {editingId === cp.id ? (
                        <div className="btn-group action-group" role="group">
                          <button
                            type="button"
                            className="btn action-btn action-edit"
                            onClick={() => handleUpdate(cp.id)}
                            disabled={loading}
                            title="Save"
                          >
                            <i className="fa-solid fa-check" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="btn action-btn action-delete"
                            onClick={cancelEdit}
                            disabled={loading}
                            title="Cancel"
                          >
                            <i className="fa-solid fa-xmark" aria-hidden="true" />
                          </button>
                        </div>
                      ) : (
                        <div className="btn-group action-group" role="group">
                          <button
                            type="button"
                            className="btn action-btn action-edit"
                            onClick={() => startEdit(cp)}
                            disabled={loading}
                            title="Edit details"
                          >
                            <i className="fa-solid fa-pen" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="btn action-btn action-delete"
                            onClick={() => handleDelete(cp.id)}
                            disabled={loading}
                            title="Remove assignment"
                          >
                            <i className="fa-solid fa-trash" aria-hidden="true" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                  ))
                ) : (
                  pendingPersons.map(cp => (
                  <tr key={cp.tempId}>
                    <td>{cp.personFirstName} {cp.personLastName}</td>
                    <td>
                      {editingId === cp.tempId ? (
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={jobTitle}
                          onChange={(e) => setJobTitle(e.target.value)}
                        />
                      ) : (
                        cp.jobTitle || <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {editingId === cp.tempId ? (
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={department}
                          onChange={(e) => setDepartment(e.target.value)}
                        />
                      ) : (
                        cp.department || <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {editingId === cp.tempId ? (
                        <input
                          type="email"
                          className="form-control form-control-sm"
                          value={workingEmail}
                          onChange={(e) => setWorkingEmail(e.target.value)}
                        />
                      ) : (
                        cp.workingEmail || <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {editingId === cp.tempId ? (
                        <div className="btn-group action-group" role="group">
                          <button
                            type="button"
                            className="btn action-btn action-edit"
                            onClick={() => handleUpdatePending(cp.tempId)}
                            title="Save"
                          >
                            <i className="fa-solid fa-check" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="btn action-btn action-delete"
                            onClick={cancelEdit}
                            title="Cancel"
                          >
                            <i className="fa-solid fa-xmark" aria-hidden="true" />
                          </button>
                        </div>
                      ) : (
                        <div className="btn-group action-group" role="group">
                          <button
                            type="button"
                            className="btn action-btn action-edit"
                            onClick={() => startEditPending(cp)}
                            title="Edit details"
                          >
                            <i className="fa-solid fa-pen" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="btn action-btn action-delete"
                            onClick={() => handleDeletePending(cp.tempId)}
                            title="Remove assignment"
                          >
                            <i className="fa-solid fa-trash" aria-hidden="true" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerPersons;
